import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notificationsApi, type Notification } from '../api';
import { t } from '../i18n';
import { cx } from '../lib/cx';
import { formatRelative } from '../lib/date';
import { errorText } from '../lib/errors';
import { toast } from '../stores/toast';
import { DutyWarning, useDutyMissing } from './DutyWarning';
import { Button, Empty, ErrorState, Loading } from './ui';

function IconBell({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6.5 2 6.5H4S6 14 6 9z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}

export const NOTIF_KEYS = { count: ['notif', 'count'], list: ['notif', 'list'] } as const;

/** Відкрити сповіщення: позначити прочитаним і перейти за посиланням. */
export function useOpenNotification() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (n: Notification) => {
    if (!n.read_at) {
      try {
        await notificationsApi.read({ ids: [n.id] });
      } catch (e) {
        toast.error(errorText(e));
      }
      void qc.invalidateQueries({ queryKey: ['notif'] });
    }
    if (n.link) navigate(n.link);
  };
}

/** Один рядок сповіщення: непрочитане — жирним і з крапкою. */
export function NotificationItem({ n, onOpen }: { n: Notification; onOpen: (n: Notification) => void }) {
  const unread = !n.read_at;
  return (
    <button type="button" onClick={() => onOpen(n)} className="w-full text-left flex gap-3 px-3 py-2.5 rounded-lg hover:bg-hov">
      <i className={cx('dot mt-1.5 flex-none', unread ? 'dot-accent' : 'dot-dim')} aria-hidden="true" />
      <span className="flex flex-col min-w-0 gap-0.5">
        <span className="text-xs text-lab">
          {t(`notif.kind.${n.kind}`)} · {formatRelative(n.created_at)}
          {unread && <span className="sr-only"> · {t('notif.new')}</span>}
        </span>
        <span className={cx('text-sm break-words', unread ? 'font-semibold text-fg' : 'text-fg')}>{n.title}</span>
        {n.body && <span className="text-sm text-lab truncate">{n.body}</span>}
      </span>
    </button>
  );
}

/** Дзвіночок у верхній панелі: лічильник непрочитаних (раз на хвилину і при поверненні у вкладку) і останні 10. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const count = useQuery({
    queryKey: NOTIF_KEYS.count,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const list = useQuery({
    queryKey: [...NOTIF_KEYS.list, 'popover'],
    queryFn: () => notificationsApi.list({ limit: 10 }),
    enabled: open,
  });
  const openItem = useOpenNotification();
  // заздалегідь підтягуємо «хто чергує», щоб попередження було в панелі з першого відкриття
  useDutyMissing();
  const [busy, setBusy] = useState(false);
  // на телефоні дзвіночок не біля правого краю екрана, тож панель прив'язуємо до екрана (відступ 16 px з обох боків),
  // а не до кнопки — інакше її лівий край виходить за екран
  const [phoneTop, setPhoneTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      setPhoneTop(r && window.innerWidth < 640 ? Math.round(r.bottom + 8) : null);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // закривається кліком поза панеллю і клавішею Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = count.data?.unread ?? 0;
  const badge = unread > 99 ? '99+' : String(unread);
  const markAll = async () => {
    setBusy(true);
    try {
      await notificationsApi.read({ all: true });
      toast.success(t('notif.markedAll'));
      await qc.invalidateQueries({ queryKey: ['notif'] });
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrap} className="relative">
      <button
        ref={btn}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `${t('notif.title')}, ${t('notif.unreadBadge', { n: unread })}` : t('notif.title')}
        title={t('notif.title')}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded border border-line bg-bg text-lab hover:text-fg hover:border-line-strong"
      >
        <IconBell />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-accent text-accent-fg text-2xs font-bold grid place-items-center tabular-nums"
          >
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={t('notif.title')}
          className={cx(
            'z-40 bg-bg border border-line rounded-xl shadow-2xl flex flex-col',
            phoneTop != null
              ? 'fixed left-4 right-4'
              : 'absolute right-0 top-12 w-[min(380px,calc(100vw-32px))] max-h-[min(560px,calc(100vh-96px))]',
          )}
          style={phoneTop != null ? { top: phoneTop, maxHeight: `calc(100dvh - ${phoneTop + 16}px)` } : undefined}
        >
          <div className="px-4 pt-3 pb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line">
            <b className="text-md font-semibold">{t('notif.title')}</b>
            {unread > 0 && <span className="text-xs text-lab">{t('notif.unreadBadge', { n: unread })}</span>}
          </div>
          <div className="overflow-y-auto p-2 flex flex-col gap-1">
            <DutyWarning compact className="mb-1" />
            {list.isPending ? (
              <Loading className="py-4 px-3" />
            ) : list.isError ? (
              <ErrorState error={list.error} className="py-4 px-3" onRetry={() => void list.refetch()} />
            ) : list.data.items.length === 0 ? (
              <Empty className="py-6 px-3 text-sm">{t('notif.empty')}</Empty>
            ) : (
              list.data.items.map((n) => (
                <NotificationItem
                  key={n.id}
                  n={n}
                  onOpen={(x) => {
                    setOpen(false);
                    void openItem(x);
                  }}
                />
              ))
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2.5 border-t border-line bg-tile rounded-b-xl">
            <Button variant="ghost" size="sm" busy={busy} disabled={unread === 0} onClick={() => void markAll()}>
              {t('notif.markAll')}
            </Button>
            <Link to="/notifications" className="link text-sm" onClick={() => setOpen(false)}>
              {t('notif.all')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
